import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Activity, ArrowLeft, ClipboardCheck, FileText, Lock, MessageSquareQuote, Mic, Scale } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { EEOCModuleCard } from '../components/eeoc/EEOCModuleCard';
import { ComplianceTrend } from '../components/eeoc/ComplianceTrend';
import { formatDateTime } from '../components/eeoc/eeocUtils';
import { EEOCCycleBanner, formatDay } from '../components/eeoc/EEOCCycleBanner';
import { AssignedOpening } from '../components/quality/AssignedOpening';

const Header = ({ data }) => (
    <div className="relative overflow-hidden rounded-2xl border border-sky-200 bg-gradient-to-br from-sky-50 to-blue-50 p-6 dark:border-sky-800 dark:from-sky-950/30 dark:to-blue-950/30">
        <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-sky-500 to-blue-600" />
        <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-4">
                <div className="rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 p-3 text-white shadow-lg"><Scale className="h-7 w-7" /></div>
                <div>
                    <h1 className="text-3xl font-bold tracking-tight" data-testid="eeoc-page-title">EEOC Training</h1>
                    <p className="mt-2 max-w-2xl text-sm md:text-base text-muted-foreground">
                        Practise live, spoken candidate conversations and learn to stay compliant with Equal Employment Opportunity law.
                    </p>
                </div>
            </div>
            <Badge variant="outline" className="border-sky-300 bg-white/70 text-sky-700 dark:bg-transparent" data-testid="eeoc-passing-score-badge">
                Pass mark {data.passing_score}
            </Badge>
        </div>
        <div className="mt-6 grid max-w-xl gap-1">
            <div className="flex flex-wrap justify-between gap-2 text-xs text-muted-foreground">
                <span data-testid="eeoc-modules-passed">{data.cycle.modules_passed}/{data.cycle.total_modules} modules passed in {data.cycle.label}</span>
                <span data-testid="eeoc-cycle-due">Due {formatDay(data.cycle.due_date)}</span>
            </div>
            <Progress value={Math.round((data.cycle.modules_passed / data.cycle.total_modules) * 100)} className="h-1.5" />
            <p className="text-[11px] text-muted-foreground">
                Everyone repeats EEOC every {data.cycle.unit}. All-time: {data.summary.completed_modules}/{data.summary.total_modules} modules passed at least once.
            </p>
        </div>
    </div>
);

const STEPS = [
    { icon: Mic, title: 'Speak with the candidate', text: `Answer ${3} times per scenario by voice, just like a real call.` },
    { icon: ClipboardCheck, title: 'Get graded rule by rule', text: 'Every EEOC rule is marked PASS or FAIL with the reason and your exact words.' },
    { icon: MessageSquareQuote, title: 'Review your coaching', text: 'See what you did right, a "Try this instead" fix and a practice drill for every mistake.' }
];

const HowItWorks = () => (
    <div className="grid gap-3 md:grid-cols-3" data-testid="eeoc-how-it-works">
        {STEPS.map(({ icon: Icon, title, text }, i) => (
            <div key={title} className="flex gap-3 rounded-xl border bg-card p-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-500/10 text-sky-600"><Icon className="h-4 w-4" /></div>
                <div>
                    <p className="text-sm font-semibold">{i + 1}. {title}</p>
                    <p className="text-xs text-muted-foreground">{text}</p>
                </div>
            </div>
        ))}
    </div>
);

const RecentSessions = ({ sessions, onOpen }) => (
    <Card data-testid="eeoc-recent-sessions">
        <CardContent className="space-y-3 p-5">
            <h2 className="flex items-center gap-2 text-base md:text-lg font-semibold"><FileText className="h-5 w-5 text-sky-600" /> Recent session reports</h2>
            {sessions.map((s) => (
                <button key={s.session_id} onClick={() => onOpen(s.session_id)} className="flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left transition-colors hover:bg-muted/50" data-testid={`eeoc-recent-${s.session_id}`}>
                    <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{s.module_title}</p>
                        <p className="text-xs text-muted-foreground">{formatDateTime(s.date)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="font-semibold tabular-nums">{s.score}</span>
                        <Badge className={s.passed ? 'bg-emerald-500 hover:bg-emerald-500' : 'bg-red-500 hover:bg-red-500'}>{s.passed ? 'PASS' : 'FAIL'}</Badge>
                    </div>
                </button>
            ))}
        </CardContent>
    </Card>
);

const Denied = ({ onBack }) => (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <Card className="border-dashed" data-testid="eeoc-access-denied">
            <CardContent className="flex flex-col items-center gap-4 px-6 py-16 text-center">
                <div className="rounded-2xl bg-muted p-4 text-muted-foreground"><Lock className="h-8 w-8" /></div>
                <h2 className="text-base md:text-lg font-semibold">EEOC Training isn't enabled for you</h2>
                <p className="max-w-md text-sm text-muted-foreground">Your administrator controls access to this training. Please contact them if you think you should have access.</p>
                <Button variant="outline" onClick={onBack} data-testid="eeoc-back-to-staffing-btn"><ArrowLeft className="mr-2 h-4 w-4" /> Back to Staffing Training</Button>
            </CardContent>
        </Card>
    </motion.div>
);

const EEOCTraining = () => {
    const { api } = useAuth();
    const navigate = useNavigate();
    const [state, setState] = useState({ loading: true, data: null, history: null, denied: false });
    const [startingId, setStartingId] = useState(null);

    useEffect(() => {
        let active = true;
        Promise.all([api.get('/eeoc-training/overview'), api.get('/eeoc-training/history')])
            .then(([overview, history]) => {
                if (!active) return;
                setState({ loading: false, data: overview.data, history: history.data, denied: false });
                api.post('/eeoc-training/visit').catch(() => {});
            })
            .catch((error) => active && setState({ loading: false, data: null, history: null, denied: error.response?.status === 403 }));
        return () => { active = false; };
    }, [api]);

    const startModule = async (module, assignedTask = null) => {
        const sessionPath = (id) => `/eeoc-training/session/${id}${assignedTask ? `?qt=${assignedTask}` : ''}`;
        if (module.active_session_id) return navigate(sessionPath(module.active_session_id));
        setStartingId(module.id);
        try {
            const { data } = await api.post('/eeoc-training/sessions', { module_id: module.id });
            navigate(sessionPath(data.id));
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Could not start this module');
            setStartingId(null);
            if (assignedTask) setAssignedFailed(true);
        }
    };

    // Quality Task deep link: /eeoc-training?start=<module_id>&qt=<task_id> opens only that module
    const [searchParams, setSearchParams] = useSearchParams();
    const [assignedStart] = useState(() => searchParams.get('qt') && searchParams.get('start'));
    const [assignedFailed, setAssignedFailed] = useState(false);
    const autoStartRef = useRef(false);
    useEffect(() => {
        const moduleId = searchParams.get('start');
        if (!moduleId || !state.data || autoStartRef.current) return;
        autoStartRef.current = true;
        const assignedTask = searchParams.get('qt');
        setSearchParams({}, { replace: true });
        const module = state.data.modules.find((m) => m.id === moduleId);
        if (module) startModule(module, assignedTask);
        else if (assignedTask) setAssignedFailed(true);
    }, [searchParams, state.data]); // eslint-disable-line react-hooks/exhaustive-deps

    const back = () => navigate('/staffing-training');
    const { data, history } = state;

    if (assignedStart) {
        return <MainLayout><AssignedOpening failed={assignedFailed || (!state.loading && !state.data)} label="EEOC module" /></MainLayout>;
    }

    return (
        <MainLayout>
            <div className="space-y-8 p-6" data-testid="eeoc-training-page">
                <button onClick={back} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground" data-testid="eeoc-breadcrumb-back">
                    <ArrowLeft className="h-4 w-4" /> Staffing Training
                </button>
                {state.loading ? (
                    <div className="space-y-6"><Skeleton className="h-40 rounded-2xl" /><Skeleton className="h-72 rounded-2xl" /></div>
                ) : state.denied ? (
                    <Denied onBack={back} />
                ) : !data ? (
                    <p className="text-sm text-red-600">Could not load EEOC Training. Please refresh the page.</p>
                ) : (
                    <div className="space-y-8">
                        <Header data={data} />
                        <EEOCCycleBanner cycle={data.cycle} />
                        <HowItWorks />
                        <Card data-testid="eeoc-trend-card">
                            <CardContent className="space-y-4 p-5">
                                <h2 className="flex items-center gap-2 text-base md:text-lg font-semibold"><Activity className="h-5 w-5 text-sky-600" /> Your compliance trend</h2>
                                <ComplianceTrend points={history.points} summary={history.summary} passingScore={data.passing_score} />
                            </CardContent>
                        </Card>
                        <section className="space-y-4">
                            <h2 className="text-xl font-semibold">Training modules</h2>
                            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4" data-testid="eeoc-module-list">
                                {data.modules.map((m, i) => (
                                    <EEOCModuleCard key={m.id} module={m} index={i} onStart={startModule} loading={startingId === m.id} cycleUnit={data.cycle.unit} />
                                ))}
                            </div>
                        </section>
                        {data.recent_sessions.length > 0 && (
                            <RecentSessions sessions={data.recent_sessions} onOpen={(id) => navigate(`/eeoc-training/session/${id}`)} />
                        )}
                    </div>
                )}
            </div>
        </MainLayout>
    );
};

export default EEOCTraining;
